// src/components/forms/variants/hero/types.ts

import type { 
  BaseFormProps, 
  ContentConfig, 
  ThemeConfig,
  FeatureListItemConfig,
  PrivacyConfig,
  BadgeConfig,
  SubtitleConfig
} from '~/components/forms/base/types'
import { siteConfig } from '~/lib/config/site'

/**
 * Hero-specific theme configuration
 */
export interface HeroThemeConfig extends ThemeConfig {
  /** Optional background configuration */
  background?: {
    className?: string
    style?: React.CSSProperties
  }
}

/**
 * Props for the HeroEmailCapture component
 */
export interface HeroEmailCaptureProps extends Omit<BaseFormProps, 'theme' | 'content'> {
  /** Content configuration extending base content */
  content: ContentConfig & {
    /** Optional badge configuration */
    badge?: BadgeConfig
    /** Optional subtitle configuration */
    subtitle?: SubtitleConfig
    /** Optional feature list */
    features?: FeatureListItemConfig[]
    /** Optional privacy notice configuration */
    privacy?: PrivacyConfig
  }
  
  /** Theme configuration extending base theme */
  theme?: HeroThemeConfig
}

/**
 * Default preset configuration
 */
export const HERO_PRESET: Partial<HeroEmailCaptureProps> = {
  theme: {
    variant: 'hero',
    layout: 'inline',
    size: 'lg',
    theme: 'light',
    className: {
      container: 'max-w-4xl mx-auto',
      form: 'rounded-2xl bg-white dark:bg-gray-900 shadow-2xl',
      input: 'w-full rounded-lg',
      button: 'rounded-lg bg-indigo-600 text-white'
    }
  },
  content: {
    title: {
      text: 'Join AdSights',
      className: 'text-4xl font-bold tracking-tight md:text-6xl'
    },
    description: {
      text: 'Get started with your analytics journey today.',
      className: 'mt-4 text-lg text-gray-600 dark:text-gray-400'
    },
    input: {
      placeholder: 'Enter your work email',
      className: 'px-4 py-3 text-base'
    },
    button: {
      text: 'Get Started',
      loadingText: 'Processing...',
      className: 'px-6 py-3 text-base font-medium'
    },
    messages: {
      success: 'Thank you for joining! Check your email to get started.',
      error: 'Please enter a valid email address.'
    },
    privacy: {
      enabled: true,
      text: 'By continuing, you agree to our',
      privacyLink: {
        text: 'Privacy Policy',
        url: `${siteConfig.baseLinks.privacy}`
      },
      termsLink: {
        text: 'Terms of Service',
        url: `${siteConfig.baseLinks.terms}`
      }
    }
  }
} as const 